import React, { useState } from "react";
import * as Mui from "@mui/material";
import { AccountBalanceOutlined, CheckRounded, PendingActionsOutlined, PersonOutlineOutlined } from "@mui/icons-material";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { SimpleTable, ToneChip, searchFieldSx, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid } from "pages/PayRoll/Shared/statutoryUi";
import { FREQUENCY_OPTIONS, deductInLabel, optionLabel } from "pages/PayRoll/Configuration/StatutoryConfiguration/LWF/lwfConfigData";
import { versionOn } from "pages/PayRoll/Statutory/LWF/lwfEmployeesData";
import { PAYMENT_TONES, formatDay, money, num, totalsOf } from "./lwfReportsData";

// Overview (LWF3): month totals, one row per report (each opens its tab)
// and LWF by site — what was deducted this month and when it must be paid.

const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, whiteSpace: "nowrap" };
const totalCellSx = { ...tableCellSx, fontWeight: designWeights.semibold, borderTop: `1px solid ${designColors.slate300}` };

export const LwfOverviewTab = ({ monthInfo, rows, sites, periods, issues, goTo }) => {
  const [query, setQuery] = useState("");
  const totals = totalsOf(rows);
  const monthPeriods = periods.filter((period) => period.period_key === monthInfo.value);
  const toPay = monthPeriods.reduce((sum, period) => sum + Math.max(0, period.balance), 0);
  const blocking = issues.filter((item) => item.severity === "Blocking").length;
  const warnings = issues.filter((item) => item.severity === "Warning").length;
  const infos = issues.filter((item) => item.severity === "Info").length;

  const open = periods.filter((period) => period.balance > 0);
  const settled = monthPeriods.filter((period) => period.balance <= 0);
  const reports = [
    {
      report: "LWF Register",
      site: "All sites",
      details: `${num(totals.count)} covered · ${num(totals.notCovered)} not covered${totals.held ? ` · ${totals.held} held` : ""} · totals match payroll`,
      status: "Available",
      tone: "green",
      action: "View · Download",
      tab: "register",
    },
    ...open.map((period) => ({
      report: "Payments",
      site: period.site,
      details:
        period.status === "Partly paid"
          ? `${period.period_label} · ${money(period.balance)} short`
          : `${period.period_label} · ${money(period.payable)} · due ${formatDay(period.due)}`,
      status: period.status,
      tone: PAYMENT_TONES[period.status],
      action: "View · Record payment",
      tab: "payments",
    })),
    ...(settled.length
      ? [
          {
            report: "Payments",
            site: settled.map((period) => period.site).join(", "),
            details: `${monthInfo.label} · all paid`,
            status: "Paid",
            tone: "green",
            action: "View",
            tab: "payments",
          },
        ]
      : []),
    {
      report: "Validation",
      site: "All sites",
      details: `${blocking} blocking · ${warnings} warning${warnings === 1 ? "" : "s"} · ${infos} info`,
      status: blocking ? "Action needed" : "All clear",
      tone: blocking ? "red" : "green",
      action: "View",
      tab: "validation",
    },
  ];
  const needle = query.trim().toLowerCase();
  const shown = needle ? reports.filter((item) => Object.values(item).some((value) => String(value).toLowerCase().includes(needle))) : reports;

  // LWF by site for the month.
  const bySite = sites.map((site) => {
    const siteRows = rows.filter((row) => row.site_name === site.name);
    const siteTotals = totalsOf(siteRows);
    const version = versionOn(site, `${monthInfo.value}-01`);
    const period = monthPeriods.find((item) => item.site === site.name);
    let status = period ? period.status : "—";
    let tone = period ? PAYMENT_TONES[period.status] : "slate";
    if (!version && siteRows.length) {
      status = `Site not set up · ${siteRows.length} employees`;
      tone = "red";
    } else if (version && version.applicability !== "COVERED") {
      status = optionLabel([{ value: "NOT_APPLICABLE", label: "Not applicable" }, { value: "EXEMPT", label: "Exempt" }], version.applicability);
      tone = "slate";
    } else if (version && !period) {
      status = "Not a deduction month";
      tone = "slate";
    }
    return {
      site: site.name,
      deducted: version && version.applicability === "COVERED" ? `${optionLabel(FREQUENCY_OPTIONS, version.frequency)} · ${deductInLabel(version)}` : "—",
      covered: siteTotals.count,
      employee: siteTotals.employee,
      employer: siteTotals.employer,
      total: siteTotals.total,
      due: period ? formatDay(period.due) : "—",
      status,
      tone,
    };
  });

  return (
    <Mui.Stack spacing={2}>
      <StatCardGrid
        cards={[
          { key: "covered", title: "Employees covered", value: num(totals.count), icon: CheckRounded, tone: "green" },
          { key: "employee", title: "Employee share", value: money(totals.employee), icon: PersonOutlineOutlined, tone: "purple" },
          { key: "employer", title: "Employer share", value: money(totals.employer), icon: AccountBalanceOutlined, tone: "amber" },
          { key: "topay", title: "To pay", value: money(toPay), icon: PendingActionsOutlined, tone: "pink" },
        ]}
      />
      <Mui.Box sx={detailCardSx}>
        <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
          <Mui.Typography sx={designType.sectionHeading}>Reports — {monthInfo.label}</Mui.Typography>
          <Mui.TextField size="small" placeholder="Search" value={query} onChange={(event) => setQuery(event.target.value)} sx={searchFieldSx} />
        </Mui.Stack>
        <SimpleTable
          fit
          heads={[
            { label: "Report", width: 150 },
            { label: "Site", width: 200 },
            { label: "Details", width: 320 },
            { label: "Status", width: 130 },
            { label: "Action", width: 160 },
          ]}
        >
          {shown.map((item) => (
            <Mui.TableRow key={`${item.report}-${item.site}-${item.details}`} hover onClick={() => goTo(item.tab)} sx={{ cursor: "pointer" }}>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.medium }}>{item.report}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{item.site}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{item.details}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <ToneChip label={item.status} tone={item.tone} />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Box component="span" sx={linkSx}>{item.action}</Mui.Box>
              </Mui.TableCell>
            </Mui.TableRow>
          ))}
        </SimpleTable>
        <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>Reports are live views of the finalised payroll. View any report on screen before downloading.</Mui.Typography>
      </Mui.Box>

      <Mui.Box sx={detailCardSx}>
        <Mui.Typography sx={designType.sectionHeading}>LWF by site — {monthInfo.label}</Mui.Typography>
        <Mui.Typography sx={{ ...designType.helperText, mt: 0.25, mb: 1.5 }}>Deducted this month and when it must be paid</Mui.Typography>
        <SimpleTable
          fit
          heads={[
            { label: "Site", width: 180 },
            { label: "Deducted", width: 150 },
            { label: "Covered", width: 80, align: "right" },
            { label: "Employee (₹)", width: 110, align: "right" },
            { label: "Employer (₹)", width: 110, align: "right" },
            { label: "Total Payable (₹)", width: 130, align: "right" },
            { label: "Payment Due", width: 120 },
            { label: "Status", width: 200 },
          ]}
        >
          {bySite.map((item) => (
            <Mui.TableRow key={item.site}>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{item.site}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{item.deducted}</Mui.TableCell>
              <Mui.TableCell align="right" sx={tableCellSx}>{num(item.covered)}</Mui.TableCell>
              <Mui.TableCell align="right" sx={tableCellSx}>{num(item.employee)}</Mui.TableCell>
              <Mui.TableCell align="right" sx={tableCellSx}>{num(item.employer)}</Mui.TableCell>
              <Mui.TableCell align="right" sx={tableCellSx}>{num(item.total)}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{item.due}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{item.status === "—" ? "—" : <ToneChip label={item.status} tone={item.tone} />}</Mui.TableCell>
            </Mui.TableRow>
          ))}
          <Mui.TableRow>
            <Mui.TableCell sx={totalCellSx}>Total</Mui.TableCell>
            <Mui.TableCell sx={totalCellSx} />
            <Mui.TableCell align="right" sx={totalCellSx}>{num(totals.count)}</Mui.TableCell>
            <Mui.TableCell align="right" sx={totalCellSx}>{num(totals.employee)}</Mui.TableCell>
            <Mui.TableCell align="right" sx={totalCellSx}>{num(totals.employer)}</Mui.TableCell>
            <Mui.TableCell align="right" sx={totalCellSx}>{num(totals.total)}</Mui.TableCell>
            <Mui.TableCell sx={totalCellSx} />
            <Mui.TableCell sx={totalCellSx} />
          </Mui.TableRow>
        </SimpleTable>
        {totals.outstanding ? (
          <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
            Employee share includes {money(totals.outstanding)} not yet recovered from employees — still part of the payable to the Board.
          </Mui.Typography>
        ) : null}
      </Mui.Box>
    </Mui.Stack>
  );
};
